<template>
  <el-dialog title="微信扫码支付" :visible.sync="visible" width="380px"
             :close-on-click-modal="false" @closed="handleClosed">
    <div class="pay-dialog">
      <div class="pay-amount">
        <span class="amount-label">支付金额</span>
        <span class="amount-value">¥{{ amountText }}</span>
      </div>

      <!-- 二维码区域 -->
      <div class="qr-stage">
        <div v-if="loading" class="qr-state">
          <i class="el-icon-loading"></i>
          <span>正在生成支付二维码...</span>
        </div>
        <div v-else-if="expired" class="qr-state">
          <i class="el-icon-refresh"></i>
          <span>二维码已过期</span>
          <el-button type="primary" size="small" @click="createPayment">刷新二维码</el-button>
        </div>
        <div v-show="!loading && !expired" ref="qrBox" class="qr-box"></div>
      </div>

      <template v-if="!loading && !expired">
        <div class="qr-tip">请使用微信「扫一扫」完成支付</div>
        <div class="qr-countdown">
          二维码有效期：<em>{{ countdownText }}</em>
        </div>
      </template>
      <div class="qr-notice">支付完成后页面会自动刷新，请勿重复支付</div>
    </div>
  </el-dialog>
</template>
<script>
import api from '@/common/api';

// 二维码有效期，与后端 paymentRecordsEntity.timeExpire（下单时间 + 30 分钟）保持一致
const EXPIRE_SECONDS = 30 * 60;
// 结果查询间隔：后端 resultRequest 使用 30 秒全局粗粒度锁，间隔过密会互相返回「系统繁忙」
const POLL_INTERVAL = 3000;

export default {
  name: 'PayQrcodeDialog',
  data() {
    return {
      visible: false,
      loading: false,
      expired: false,
      // 展示用金额
      amount: '0.00',
      // 本次下单参数
      payParams: {},
      paymentNo: '',
      orderNo: '',
      countdown: EXPIRE_SECONDS,
      countdownTimer: null,
      pollTimer: null
    }
  },
  computed: {
    amountText() {
      return this.amount || '0.00';
    },
    countdownText() {
      const m = Math.floor(this.countdown / 60);
      const s = this.countdown % 60;
      return (m < 10 ? '0' + m : m) + ':' + (s < 10 ? '0' + s : s);
    }
  },
  beforeDestroy() {
    this.clearTimers();
  },
  methods: {
    /**
     * 打开支付弹层
     * @param {Object} params
     *   orderNo    {String}  可选，已有订单续付时传（后端会自动重建金额与订单行）
     *   totalPrice {String}  首次下单必传，总金额
     *   items      {Array}   首次下单必传，[{commodityCode, quantity, price}]
     *   amount     {String}  展示用金额
     */
    open(params) {
      const data = params || {};
      this.payParams = data;
      this.amount = data.amount || data.totalPrice || '0.00';
      this.orderNo = data.orderNo || '';
      this.paymentNo = '';
      this.expired = false;
      this.countdown = EXPIRE_SECONDS;
      this.visible = true;
      this.createPayment();
    },
    // 下单并渲染二维码
    createPayment() {
      this.clearTimers();
      this.loading = true;
      this.expired = false;
      const data = {
        // 后端用它做 1 秒防重锁，每次下单必须换新值
        requestUuid: Date.now() + '' + Math.floor(Math.random() * 100000),
        paymentMethod: 'wechat',
        // 2 = 网页二维码支付
        paymentClientType: '2'
      };
      if (this.orderNo) {
        // 续付分支：后端按订单重新计算金额与订单行，无需再传 totalPrice/items
        data.orderNo = this.orderNo;
      } else {
        data.totalPrice = this.payParams.totalPrice;
        data.items = this.payParams.items;
      }
      api.order_payment_request(data).then((r) => {
        if (r.data.code === '200' && r.data.data) {
          const res = r.data.data;
          this.paymentNo = res.paymentNo;
          this.orderNo = res.orderNo;
          this.loading = false;
          this.$nextTick(() => {
            this.renderQrcode(res.prepayId);
            this.countdown = EXPIRE_SECONDS;
            this.startCountdown();
            this.startPolling();
          });
        } else {
          this.loading = false;
          this.$message.error(r.data.msg || '发起支付失败，请稍后重试');
          this.visible = false;
        }
      }).catch(() => {
        // 401 已由 common/api.js 统一跳转登录页
        this.loading = false;
        this.visible = false;
      });
    },
    // 渲染二维码：门户端 prepayId 即微信 Native 下单返回的二维码内容（code_url）
    renderQrcode(text) {
      const box = this.$refs.qrBox;
      if (!box) {
        return;
      }
      if (!window.QRCode) {
        this.$message.error('二维码组件加载失败，请刷新页面后重试');
        this.visible = false;
        return;
      }
      // 容器必须先清空，否则刷新二维码会叠加
      box.innerHTML = '';
      // 该库挂在全局 window.QRCode 上，走 window 访问避免 ESLint no-undef
      new window.QRCode(box, {
        text: text,
        width: 200,
        height: 200,
        correctLevel: window.QRCode.CorrectLevel.M
      });
    },
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(() => {
        this.queryPayResult(false);
      }, POLL_INTERVAL);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    startCountdown() {
      this.stopCountdown();
      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown -= 1;
        }
        if (this.countdown <= 0) {
          this.stopCountdown();
          this.stopPolling();
          // 过期前再查一次，避免刚好落在两次轮询之间支付成功
          this.queryPayResult(true);
        }
      }, 1000);
    },
    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    // 查询支付结果
    queryPayResult(isFinal) {
      if (!this.paymentNo) {
        return;
      }
      api.order_payment_result({ paymentNo: this.paymentNo }).then((r) => {
        if (r.data.code === '200' && r.data.data) {
          const status = Number(r.data.data.paymentStatus);
          // 2 支付成功
          if (status === 2) {
            this.handlePaid();
            return;
          }
          // 3 支付失败/订单已关闭
          if (status === 3) {
            this.stopPolling();
            this.stopCountdown();
            if (isFinal) {
              this.expired = true;
            } else {
              this.$message.error('支付失败或订单已关闭，请重新下单');
              this.visible = false;
            }
            return;
          }
        }
        if (isFinal) {
          this.expired = true;
        }
      }).catch(() => {
        // 401 已由 common/api.js 统一跳转登录页；其余异常留给下一次轮询
        if (isFinal) {
          this.expired = true;
        }
      });
    },
    handlePaid() {
      this.clearTimers();
      this.$message.success('支付成功');
      this.visible = false;
      // 由页面自行刷新列表或详情
      this.$emit('success', { orderNo: this.orderNo });
    },
    clearTimers() {
      this.stopPolling();
      this.stopCountdown();
    },
    handleClosed() {
      this.clearTimers();
      this.paymentNo = '';
      this.expired = false;
    }
  }
};
</script>
<style scoped>
.pay-dialog {
  text-align: center;
}
.pay-amount {
  margin-bottom: 18px;
}
.amount-label {
  font-size: 13px;
  color: #909399;
  margin-right: 8px;
}
.amount-value {
  font-size: 24px;
  color: #F56C6C;
  font-weight: bold;
}
.qr-stage {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 216px;
  background-color: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;
}
.qr-box {
  padding: 8px;
  background-color: #fff;
}
.qr-box >>> img,
.qr-box >>> canvas {
  display: block;
}
.qr-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #909399;
}
.qr-state i {
  font-size: 26px;
  color: #2B7EF5;
}
.qr-tip {
  font-size: 14px;
  color: #303133;
  margin-top: 16px;
}
.qr-countdown {
  font-size: 13px;
  color: #909399;
  margin-top: 8px;
}
.qr-countdown em {
  font-style: normal;
  color: #2B7EF5;
  font-weight: 500;
}
.qr-notice {
  font-size: 12px;
  color: #c0c4cc;
  margin-top: 14px;
}
</style>
